import React, { useEffect, useState } from 'react';

import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import { BookType, PopType, Title } from '../../@types/Calculations';
import { RacePersona } from '../../@types/RacesResponse';
import { SimState } from '../../@types/State';
import { useIntelState } from '../../context/IntelState';
import { draftRates, rituals } from '../../helpers/GameAgeData';

const spells: string[] = [
  'hi',
  'ia',
  'pat',
  'mp',
  'gp',
  'gre',
  'fan',
  'bl',
  'agr',
  'fl',
];

const ranks: Title[] = [
  'Peasant',
  'Knight',
  'Lord',
  'Baron',
  'Viscount',
  'Count',
  'Marquis',
  'Duke',
  'Prince',
];

interface Props {
  simState: SimState;
  updateSimState: (field: string, value: any) => void;
}

const SimulationForm: React.FC<Props> = ({ simState, updateSimState }) => {
  const { intelState } = useIntelState();
  const [races, setRaces] = useState<RacePersona[] | undefined>(undefined);

  const handleSectionChange = (
    section: 'buildPercentages' | 'science',
    field: string,
    e: any
  ) => {
    const newSection = {
      ...simState[section],
      [field]: parseFloat(e.target.value || 0),
    };
    updateSimState(section, newSection);
  };

  useEffect(() => {
    const races =
      intelState.server === 2 ? intelState.racesGen : intelState.racesWol;
    setRaces(races);
  }, [intelState.racesGen, intelState.racesWol, intelState.server]);

  const buildingSelector = (field: string) => {
    const buildPercentages = simState.buildPercentages ?? simState.build;
    return (
      <Grid item xs={1}>
        <TextField
          fullWidth
          size='small'
          type='number'
          label={field}
          InputProps={{ inputProps: { min: 0, max: 100 } }}
          value={buildPercentages[field]}
          onChange={(e) => handleSectionChange('buildPercentages', field, e)}
        />
      </Grid>
    );
  };

  const scienceSelector = (field: BookType) => {
    return (
      <Grid item xs={1}>
        <TextField
          fullWidth
          size='small'
          type='number'
          label={field}
          InputProps={{ inputProps: { min: 0, max: 100 } }}
          value={simState.science[field]}
          onChange={(e) => handleSectionChange('science', field, e)}
        />
      </Grid>
    );
  };

  const popSelector = (field: PopType) => {
    return (
      <Grid item xs={1}>
        <TextField
          fullWidth
          size='small'
          type='number'
          label={field}
          value={simState[field]}
          InputProps={{ inputProps: { min: 0 } }}
          onChange={(e) => updateSimState(field, parseInt(e.target.value))}
        />
      </Grid>
    );
  };

  const ranksSelector = () => {
    return (
      <FormControl fullWidth size='small'>
        <InputLabel id={`rank-select-label`}>rank</InputLabel>
        <Select
          id={`rank-select`}
          name={`rank-select`}
          labelId={`rank-select-label`}
          value={simState.title}
          label='rank'
          onChange={(e) => {
            updateSimState('title', e.target.value);
          }}
        >
          {ranks.map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const dropDownSelector = (field: 'ritual' | 'draftRate', arr: any[]) => {
    return (
      <FormControl fullWidth size='small'>
        <InputLabel id={`${field}-select-label`}>{field}</InputLabel>
        <Select
          id={`${field}-select`}
          name={`${field}-select`}
          labelId={`${field}-select-label`}
          value={simState[field]}
          label={field}
          onChange={(e) => {
            updateSimState(field, e.target.value);
          }}
        >
          {arr.map((item, i) => (
            <MenuItem key={i} value={JSON.stringify(item)}>
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const spellSelector = (key: string) => {
    return (
      <Grid item xs={1} key={key}>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={simState.spells.includes(key)} />}
            label={key}
            onClick={() => {
              let newArr: string[];
              if (simState.spells.includes(key)) {
                newArr = simState.spells.filter((x) => x !== key);
              } else {
                newArr = [...simState.spells];
                newArr.push(key);
              }
              updateSimState('spells', newArr);
            }}
          />
        </FormGroup>
      </Grid>
    );
  };

  const getTotalBuild = () => {
    const buildPercentages = simState.buildPercentages ?? simState.build;
    return (
      buildPercentages.barren +
      buildPercentages.banks +
      buildPercentages.barracks +
      buildPercentages.armouries +
      buildPercentages.castles +
      buildPercentages.dungeons +
      buildPercentages.farms +
      buildPercentages.forts +
      buildPercentages.guilds +
      buildPercentages.thievesDens +
      buildPercentages.towers +
      buildPercentages.trainingGrounds +
      buildPercentages.homes +
      buildPercentages.hospitals +
      buildPercentages.libraries +
      buildPercentages.mills +
      buildPercentages.stables +
      buildPercentages.universities +
      buildPercentages.watchTowers
    );
  };
  const colProp = { xs: 3, sm: 6 };
  return (
    <div className='SimulationForm'>
      <Box>
        <Typography variant='body2'>Throne</Typography>
        <Grid container spacing={2} mt={1} columns={{ xs: 4, sm: 8 }}>
          <Grid item xs={2}>
            {races?.length && (
              <FormControl fullWidth size='small'>
                <InputLabel id='race-combo-label'>race combo</InputLabel>
                <Select
                  name='race-combo'
                  labelId='race-combo-label'
                  value={simState.raceCombo ?? ''}
                  label='race combo'
                  onChange={(e) => {
                    updateSimState('raceCombo', e.target.value);
                  }}
                >
                  {races?.map((item: RacePersona, i) => (
                    <MenuItem
                      key={i}
                      value={`${item.race} ${item.personality}`}
                    >
                      {item.race} {item.personality}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={2}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='acres'
              value={simState['acres']}
              InputProps={{ inputProps: { min: 0 } }}
              onChange={(e) =>
                updateSimState('acres', parseInt(e.target.value))
              }
            />
          </Grid>
          <Grid item xs={2}>
            {ranksSelector()}
          </Grid>
          <Grid item xs={2}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='BE'
              value={simState['be']}
              InputProps={{ inputProps: { min: 0, max: 200 } }}
              onChange={(e) => updateSimState('be', parseFloat(e.target.value))}
            />
          </Grid>

          {/* <Grid item xs={2}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='food'
              value={simState['food']}
              InputProps={{ inputProps: { min: 0 } }}
              onChange={(e) => updateSimState('food', parseInt(e.target.value))}
            />
          </Grid> */}
          {/* <Grid item xs={2}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='runes'
              value={simState['runes']}
              InputProps={{ inputProps: { min: 0 } }}
              onChange={(e) =>
                updateSimState('runes', parseInt(e.target.value))
              }
            />
          </Grid> */}
        </Grid>
      </Box>
      <Box mt={2}>
        <Typography variant='body2'>Ritual</Typography>
        <Grid container spacing={2} mt={1} columns={{ xs: 2 }}>
          <Grid item xs={1}>
            {dropDownSelector('ritual', rituals)}
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='RitualEffect'
              value={simState['ritEff']}
              InputProps={{ inputProps: { min: 50, max: 200 } }}
              onChange={(e) =>
                updateSimState('ritEff', parseFloat(e.target.value))
              }
            />
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <Typography variant='body2'>Spells</Typography>
        <Grid container mt={1} columns={{ xs: 3, sm: 5, md: 5, lg: 10 }}>
          {spells.map((s) => spellSelector(s))}
        </Grid>
      </Box>
      <Box mt={2}>
        <Typography variant='body2'>Military</Typography>
        <Grid container spacing={2} mt={1} columns={colProp}>
          {popSelector('wizards')}
          {popSelector('soldiers')}
          {popSelector('oSpecs')}
          {popSelector('dSpecs')}
          {popSelector('elites')}
          {popSelector('thieves')}
          {popSelector('prisoners')}
          {popSelector('horses')}
          {popSelector('soldiersTraining')}
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='targetDraft'
              InputProps={{ inputProps: { min: 20, max: 100 } }}
              value={simState['targetDraft']}
              onChange={(e) =>
                updateSimState('targetDraft', parseInt(e.target.value))
              }
            />
          </Grid>
          <Grid item xs={1}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='wages'
              InputProps={{ inputProps: { min: 20, max: 200 } }}
              value={simState['wages']}
              onChange={(e) =>
                updateSimState('wages', parseInt(e.target.value))
              }
            />
          </Grid>
          <Grid item xs={1}>
            {dropDownSelector('draftRate', draftRates)}
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <Typography variant='body2'>
          Survey (%) {`Total = ${getTotalBuild()}%`}
        </Typography>
        <Grid container spacing={2} mt={1} columns={colProp}>
          {buildingSelector('barren')}
          {buildingSelector('homes')}
          {buildingSelector('farms')}
          {buildingSelector('mills')}
          {buildingSelector('banks')}
          {buildingSelector('trainingGrounds')}
          {buildingSelector('armouries')}
          {buildingSelector('barracks')}
          {buildingSelector('forts')}
          {buildingSelector('castles')}
          {buildingSelector('hospitals')}
          {buildingSelector('guilds')}
          {buildingSelector('towers')}
          {buildingSelector('thievesDens')}
          {buildingSelector('watchTowers')}
          {buildingSelector('universities')}
          {buildingSelector('libraries')}
          {buildingSelector('stables')}
          {buildingSelector('dungeons')}
        </Grid>
      </Box>
      <Box mt={2}>
        <Typography variant='body2'>Science</Typography>
        <Grid container spacing={2} mt={1} columns={colProp}>
          {scienceSelector('alchemy')}
          {scienceSelector('tools')}
          {scienceSelector('housing')}
          {scienceSelector('production')}
          {scienceSelector('bookkeeping')}
          {scienceSelector('artisan')}
          {scienceSelector('strategy')}
          {scienceSelector('siege')}
          {scienceSelector('tactics')}
          {scienceSelector('valor')}
          {scienceSelector('heroism')}
          {scienceSelector('resilience')}
          {scienceSelector('crime')}
          {scienceSelector('channeling')}
          {scienceSelector('shielding')}
          {scienceSelector('cunning')}
          {scienceSelector('sorcery')}
          {scienceSelector('finesse')}
        </Grid>
      </Box>
    </div>
  );
};

export default SimulationForm;
